import { useCallback, useEffect, useMemo, useState, type ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Button, Combobox, EmptyState, Panel, Skeleton, type ComboboxOption } from '@/components/ui';
import { Notice } from '@/components/ui/Notice';
import { DashboardFilterBar, FilterSlot } from '@/components/dashboards/DashboardFilterBar';
import { ApiError, api } from '@/lib/api';
import type { SalesTargetsView } from '@/lib/types';
import { useAuth } from '@/lib/use-auth';
import { useCycleOptions } from '@/lib/use-cycle-options';
import { SalesTargetsSummary } from './SalesTargetsSummary';
import { RegionalTargetsChart } from './RegionalTargetsChart';
import { ProjectProgressChart } from './ProjectProgressChart';
import { SalesTargetPoolsCard } from './SalesTargetPoolsCard';
import { SalesTargetSettingsCard } from './SalesTargetSettingsCard';
import { SalesTargetsTable } from './SalesTargetsTable';
import { SalesTargetsCards } from './SalesTargetsCards';
import { SalesTargetsExportButton } from './SalesTargetsExportButton';

/**
 * THE **Sales targets** TAB OF THE MANAGEMENT DASHBOARD (owner 2026-10-28).
 *
 * The shell: the cycle picker, the three states, the Google Sheet link, and the
 * composition of the cards below it. Every figure comes from one read,
 * `GET /sales-targets?cycleId=`; every child that writes calls `onChanged`,
 * which bumps `reloadToken` and refetches that read.
 *
 * ⚠️ **`stCycle` ABSENT = THE SERVER'S CHOICE** (the latest cycle that has lines,
 * else the current one). The picker then shows the cycle the response names,
 * so the control and the figures can never disagree. Prefixed `st` because the
 * Management tabs share one query string.
 *
 * ⚠️ **NO PERMISSION GATE FOR READ HERE.** `ManagementDashboardPage` only offers
 * this tab to `canRead('sales-targets')`; the server's 403 is the boundary and
 * arrives here as the danger Notice. Edits follow `canUpdate('sales-targets')`.
 */

const PARAM_CYCLE = 'stCycle';

/** A positive integer id from the URL, else `undefined` (= let the server choose). */
function parseCycleId(raw: string | null): number | undefined {
  if (raw === null || !/^[1-9]\d*$/.test(raw)) return undefined;
  return Number(raw);
}

export function SalesTargetsPanel(): ReactElement {
  const { ability } = useAuth();
  const canEdit = ability.canUpdate('sales-targets');

  const [searchParams, setSearchParams] = useSearchParams();
  const requestedCycleId = parseCycleId(searchParams.get(PARAM_CYCLE));

  const { cycles, loading: cyclesLoading } = useCycleOptions();

  const [view, setView] = useState<SalesTargetsView | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadToken, setReloadToken] = useState(0);

  useEffect(() => {
    const controller = new AbortController();
    api
      .getSalesTargets(requestedCycleId, controller.signal)
      .then((result) => {
        if (controller.signal.aborted) return;
        setView(result);
        setError(null);
      })
      .catch((cause: unknown) => {
        if (controller.signal.aborted) return;
        setError(
          cause instanceof ApiError ? cause.message : 'The sales targets could not be loaded.',
        );
      });
    return () => controller.abort();
  }, [requestedCycleId, reloadToken]);

  const onChanged = useCallback(() => setReloadToken((token) => token + 1), []);

  const changeCycle = useCallback(
    (value: string) => {
      const next = new URLSearchParams(searchParams);
      if (value === '') next.delete(PARAM_CYCLE);
      else next.set(PARAM_CYCLE, value);
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  /* Newest cycle first, by date — cycle ids are not in date order. */
  const cycleOptions = useMemo<ComboboxOption[]>(() => {
    const options = [...cycles]
      .sort((a, b) => (a.startDate < b.startDate ? 1 : a.startDate > b.startDate ? -1 : 0))
      .map((cycle) => ({ value: String(cycle.id), label: cycle.cycleName }));
    /* The response's cycle is always offered, even if the list failed to load. */
    if (view !== null && !options.some((option) => option.value === String(view.cycle.id))) {
      options.unshift({ value: String(view.cycle.id), label: view.cycle.name });
    }
    return options;
  }, [cycles, view]);

  /* The cycle shown: the URL's pick, else the one the server answered with. */
  const cycleValue =
    requestedCycleId !== undefined
      ? String(requestedCycleId)
      : view !== null
        ? String(view.cycle.id)
        : '';

  const sheetUrl = view?.sheet.url ?? null;

  const filterBar = (
    <DashboardFilterBar
      primary={
        <>
          <FilterSlot label="Cycle" className="w-full sm:w-56">
            <Combobox
              value={cycleValue}
              onChange={changeCycle}
              options={cycleOptions}
              placeholder="Cycle"
              loading={cyclesLoading}
            />
          </FilterSlot>
          <div className="ml-auto flex items-end gap-2">
            <SalesTargetsExportButton view={view} />
            {sheetUrl !== null && (
              <Button variant="secondary" href={sheetUrl} target="_blank">
                Open Google Sheet
              </Button>
            )}
          </div>
        </>
      }
    />
  );

  if (view === null) {
    return (
      <div className="flex min-w-0 flex-col gap-4">
        {filterBar}
        {error !== null ? (
          <Notice tone="danger" role="alert">
            {error}
          </Notice>
        ) : (
          <div className="flex flex-col gap-4">
            <Skeleton className="h-24 w-full" />
            <div className="grid gap-4 lg:grid-cols-2">
              <Skeleton className="h-64 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
            <Skeleton className="h-96 w-full" />
          </div>
        )}
      </div>
    );
  }

  const isEmpty = view.lines.length === 0 && view.pools.length === 0;

  return (
    <div className="flex min-w-0 flex-col gap-4">
      {filterBar}

      {error !== null && (
        <Notice tone="danger" role="alert">
          {error}
        </Notice>
      )}

      {isEmpty ? (
        <Panel className="min-w-0">
          <EmptyState
            title={`No projects in ${view.cycle.name}`}
            description="Add projects to this cycle to set their sales targets."
          />
        </Panel>
      ) : (
        <>
          <SalesTargetsSummary view={view} />

          <div className="grid min-w-0 gap-4 lg:grid-cols-2">
            <div className="min-w-0">
              <RegionalTargetsChart regions={view.regions} asOf={view.asOf} />
            </div>
            <div className="min-w-0">
              <ProjectProgressChart view={view} />
            </div>
          </div>

          <div className="grid min-w-0 gap-4 lg:grid-cols-2">
            <div className="min-w-0">
              <SalesTargetPoolsCard view={view} canEdit={canEdit} onChanged={onChanged} />
            </div>
            <div className="min-w-0">
              <SalesTargetSettingsCard view={view} canEdit={canEdit} onChanged={onChanged} />
            </div>
          </div>

          <div className="hidden min-w-0 md:block">
            <SalesTargetsTable view={view} canEdit={canEdit} onChanged={onChanged} />
          </div>
          <div className="min-w-0 md:hidden">
            <SalesTargetsCards view={view} canEdit={canEdit} onChanged={onChanged} />
          </div>
        </>
      )}
    </div>
  );
}
